/* eslint-disable camelcase */
import axios from 'axios';

import { useServicerStore } from '../store';

const LoanService = {
  async createLoan(formValues) {
    const values = {
      ...formValues,
      servicer_id: useServicerStore.getState()?.servicerId,
      status: 'active',
      ach_type: 'off',
    };

    const response = await axios.post('/loan', values);

    return response.data;
  },

  async getLoan(id) {
    const response = await axios.get(`/loan/${id}`);

    return response.data;
  },

  async getLoans({ page, limit, lenderIds, status }) {
    const response = await axios.get('/loan', {
      params: {
        servicer_id: useServicerStore.getState()?.servicerId,
        page,
        per_page: limit,
        lender_ids: lenderIds,
        status,
      },
    });

    return response.data;
  },

  async updateLoan({ loanId, formValues }) {
    const values = {
      ...formValues,
      servicer_id: useServicerStore.getState()?.servicerId,
    };

    const response = await axios.patch(`/loan/${loanId}`, values);

    return response.data;
  },

  async getLoanPaymentSchedule({ loanId, type }) {
    const response = await axios.get(`/loan/${loanId}/schedule`, {
      params: {
        type,
      },
    });

    return response.data;
  },

  async getLoanOnboarding(loanId) {
    const response = await axios.get(`/loan/${loanId}/onboarding`);

    return response.data;
  },

  async updateLoanOnboarding({ loanId, onboardingId, status, notes }) {
    const response = await axios.put(
      `/loan/${loanId}/onboarding/${onboardingId}`,
      {
        status,
        notes,
      }
    );

    return response.data;
  },

  async getLoanBillPayments(loanId) {
    const response = await axios.get(`/loan/${loanId}/bill_payments`);

    return response.data;
  },

  async generateSetupInvoice(loanId) {
    const response = await axios.post(`/loan/${loanId}/setup`);

    return response.data;
  },

  async generatePaymentSchedule(loanId) {
    const response = await axios.post(
      `/loan/${loanId}/generate_payment_schedule`
    );

    return response.data;
  },

  async getLoanSequences(id) {
    const response = await axios.get(`/loan/${id}/sequences`);
    const data = response.data.map((sequence) => ({
      sequence,
    }));

    return data;
  },

  async getLoanPayments({ loanId }) {
    const response = await axios.get(`/loan/${loanId}/payments`);

    return response.data;
  },

  async getLoanLenders(id) {
    const response = await axios.get(`/loan/${id}/lenders`);

    return response.data;
  },

  async getLoanDocuments({ loanId, type, unassociatedWithInsurance, status }) {
    const response = await axios.get(`/loan/${loanId}/document`, {
      params: {
        type,
        unassociated_with_insurance: unassociatedWithInsurance,
        status,
      },
    });

    return response.data;
  },

  async getLoanBills({
    loanId,
    type,
    status,
    unassociatedWithInsurance,
    includeNextBillId,
    unassociatedWithClaim,
  }) {
    const response = await axios.get(`/loan/${loanId}/bills`, {
      params: {
        type,
        status,
        unassociated_with_insurance: unassociatedWithInsurance,
        include_next_bill_id: includeNextBillId,
        unassociated_with_claim: unassociatedWithClaim,
      },
    });

    return response.data;
  },

  async getLoanInsurances(loanId) {
    const response = await axios.get(`/loan/${loanId}/insurances`);

    return response.data;
  },

  async getBorrowerLoanInsurances(loanId) {
    const response = await axios.get(`/loan/${loanId}/insurance`);

    return response.data;
  },

  async getLoanProperty(loanId) {
    const response = await axios.get(`/loan/${loanId}/property`);

    return response.data;
  },

  async getLoanSummary(loanId) {
    const response = await axios.get(`/loan/${loanId}/summary`);

    return response.data;
  },

  async getLoanBorrowers(loanId) {
    const response = await axios.get(`/loan/${loanId}/borrowers`);

    return response.data;
  },

  async getLoanParticipation(loanId) {
    const response = await axios.get(`/loan/${loanId}/participation`);

    return response.data;
  },

  async forcePlace(loanId) {
    const response = await axios.post(`/loan/${loanId}/force_place`);

    return response.data;
  },

  async createPrincipalPayment({ loanId, formValues }) {
    const response = await axios.post(`/loan/${loanId}/principal_payment`, {
      ...formValues,
    });

    return response.data;
  },

  async getLoanDemands(loanId) {
    const response = await axios.get(`/loan/${loanId}/demands`);

    return response.data;
  },

  async getLoanPastDueInvoices(loanId) {
    const response = await axios.get(`/loan/${loanId}/past_due`);

    return response.data;
  },

  async getLoanClaims(loanId) {
    const response = await axios.get(`/loan/${loanId}/claims`);

    return response.data;
  },

  async createPayoffInvoice({ loanId, formValues }) {
    const response = await axios.post(
      `/loan/${loanId}/invoice/payoff`,
      formValues
    );

    return response.data;
  },

  async getLoanImpoundAnalyses(loanId) {
    const response = await axios.get(`/loan/${loanId}/impound_analysis`);

    return response.data;
  },
};

export default LoanService;
